import session from './session.js'
import cache from './cache/index.js'
import analytics from './analytics.js'
import { getApiDomain, getUrlOwner } from './index.js'

const THIRD_PARTY_PAYMENT_PATH = '/web/api/payment/third-party-payment'
const EXTRA_PARAM_GAME_CODES = ['xjlru', 'hzwru']

/**
 * Build the third-party cashier URL without changing the current page.
 */
export function buildThirdPartyPaymentUrl({
	gameData = {},
	roleInfo = {},
	goods = {},
	channel = {},
	couponId = ''
} = {}) {
	const paymentChannel = normalizePaymentChannel(channel)
	validatePaymentData(gameData, roleInfo, goods, paymentChannel)

	const gameCode = cache.getGameCode() || gameData.gameCode
	const params = {
		userId: session.getUserId(),
		gameCode,
		gameName: gameData.gameName,
		serverCode: roleInfo.serverCode,
		serverName: roleInfo.serverName,
		roleId: roleInfo.roleId,
		roleName: roleInfo.roleName,
		roleLevel: roleInfo.roleLevel,
		platform: 'web',
		packageName: 'web',
		token: session.getLoginToken(),
		productId: goods.productId,
		amount: goods.amount,
		channel: paymentChannel.channel,
		paymentType: paymentChannel.paymentType,
		couponId,
		owner: getUrlOwner()
	}
	if (goods.activityId !== undefined && goods.activityId !== null && goods.activityId !== '') {
		params.activityId = goods.activityId
	}

	if (EXTRA_PARAM_GAME_CODES.includes(gameData.gameCode)) {
		const accountId = roleInfo.object && roleInfo.object.accountId
		params.extra = JSON.stringify([accountId, roleInfo.roleId, roleInfo.serverCode, goods.diamond, 0, 0])
		params.cpOrderId = roleInfo.roleId
	}

	return `${getApiDomain()}${THIRD_PARTY_PAYMENT_PATH}?${toQuery(params)}`
}

/**
 * Persist the selected role and redirect the browser to the third-party cashier.
 */
export function openThirdPartyPayment(options = {}) {
	const roleInfo = options.roleInfo || {}
	const paymentUrl = buildThirdPartyPaymentUrl(options)
	persistRoleInfo(roleInfo)
	analytics.payment.started(buildCheckoutProperties(options))
	analytics.flush()

	if (typeof window === 'undefined') {
		throw new Error('Third-party payment can only be opened in a browser')
	}
	window.location.href = paymentUrl
	return paymentUrl
}

function buildCheckoutProperties({ gameData = {}, roleInfo = {}, goods = {}, channel = {}, couponId = '' } = {}) {
	const paymentChannel = normalizePaymentChannel(channel)
	const amount = Number(goods.amount)
	return {
		game_code: cache.getGameCode() || gameData.gameCode || null,
		product_id: goods.productId || null,
		amount: Number.isFinite(amount) ? amount : null,
		currency: goods.currency || null,
		payment_channel: paymentChannel.channel || null,
		payment_type: paymentChannel.paymentType || null,
		coupon_applied: Boolean(couponId),
		role_server_code: roleInfo.serverCode || null
	}
}

function validatePaymentData(gameData, roleInfo, goods, channel) {
	const required = [
		['gameCode', cache.getGameCode() || gameData.gameCode],
		['roleId', roleInfo.roleId],
		['serverCode', roleInfo.serverCode],
		['productId', goods.productId],
		['amount', goods.amount],
		['channel', channel.channel],
		['paymentType', channel.paymentType]
	]
	const missingField = required.find(([, value]) => value === undefined || value === null || value === '')
	if (missingField) {
		throw new Error(`Missing payment field: ${missingField[0]}`)
	}
}

function normalizePaymentChannel(channel) {
	if (typeof channel === 'string') {
		return {
			channel,
			paymentType: channel
		}
	}
	return channel || {}
}

function persistRoleInfo(roleInfo) {
	if (typeof localStorage === 'undefined') return
	localStorage.setItem('new__role_info', JSON.stringify({
		serverCode: roleInfo.serverCode,
		serverName: roleInfo.serverName,
		roleId: roleInfo.roleId,
		roleName: roleInfo.roleName
	}))
}

function toQuery(params) {
	return Object.keys(params)
		.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(normalizeValue(params[key]))}`)
		.join('&')
}

function normalizeValue(value) {
	return value === undefined || value === null ? '' : value
}
